import React, { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Nullable } from '@types'

import {
  CardImageAltInfo,
  CardImageBox,
  PopoverCardImageSkeleton,
  PopoverCardInfoSectionSkeleton,
  PopoverCardTextSkeleton,
  PopoverItemCard,
  PopoverList,
  PopoverListInner,
  PopoverListWrapper,
} from '@components/CardsList/index.styles'
import Popover from '@mui/material/Popover'

interface CardsListPopoverProps {
  buttonText: string
  popoverId: string
  items: React.ReactNode[]
  itemWidth?: number
  onLoadMore?: () => void
}

const CardsListPopover = (props: CardsListPopoverProps) => {
  const { buttonText, popoverId, items, itemWidth, onLoadMore } = props

  const { ref, inView } = useInView()
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLDivElement>>(null)

  useEffect(() => {
    if (inView && onLoadMore) {
      onLoadMore()
    }
    // eslint-disable-next-line
  }, [inView])
  const hasActiveItem = useMemo(() => items?.some(item => !!item), [items])

  if (!items.length) {
    return null
  }
  const handlePopoverClick = (e: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget as HTMLDivElement)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const isPopoverOpen = Boolean(anchorEl)
  const cardsPopoverId = isPopoverOpen ? popoverId : undefined

  return (
    <>
      <CardImageBox
        onClick={handlePopoverClick}
        $itemWidth={itemWidth}
        data-testid="more-cards-button"
        $isDisabled={!hasActiveItem}
      >
        <CardImageAltInfo seeMoreButtonView>{buttonText}</CardImageAltInfo>
      </CardImageBox>
      <Popover
        id={cardsPopoverId}
        data-testid={`${cardsPopoverId}_testid`}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          marginTop: '10px',
        }}
      >
        <PopoverListWrapper>
          <PopoverList>
            <PopoverListInner>{items}</PopoverListInner>
            {onLoadMore && (
              <PopoverItemCard ref={ref} data-testid="load-more-anchor">
                <CardImageBox>
                  <PopoverCardImageSkeleton variant="circular" />
                </CardImageBox>
                <PopoverCardInfoSectionSkeleton>
                  <PopoverCardTextSkeleton animation="wave" />
                  <PopoverCardTextSkeleton animation="wave" />
                  <PopoverCardTextSkeleton animation="wave" />
                </PopoverCardInfoSectionSkeleton>
              </PopoverItemCard>
            )}
          </PopoverList>
        </PopoverListWrapper>
      </Popover>
    </>
  )
}

export default CardsListPopover
